import React, {useRef} from "react";
import {Link, useParams} from "react-router-dom";
import {API, createApiConfig, DateUtils, Utils} from "../../utils";
import {AxiosError} from "axios";
import {useKeycloak} from "@react-keycloak/web";
import Loading, {LoadingBackdrop} from "../common/Loading";
import {CommunicationError, NotFound} from "../common/errors";
import {Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {
    ImportResultDTO,
    InputValueCreateDTO,
    InputValueDTO,
    InputValueResultDTO,
    InputVariableTaskDTO,
    ValuesInputTaskDTO,
} from "../../models/values";
import {
    CustomActionButton,
    ExpandLessActionButton,
    ExpandMoreActionButton,
    InfoActionButton,
    LinkActionButton,
} from "../common/buttons";
import {ROUTES} from "../../routes/routes";
import {InputValueForm} from "./InputValueForm";
import {ErrorNotification, InfoNotification, SuccessNotification} from "../common/notifications";
import {InputVariableDialog} from "./dialogs/InputVariableDialog";
import {InputValueHistory} from "./InputValueHistory";
import {defaultPalette} from "../../styles";
import WorkingSVG from "../../graphics/undraw/factory_dy0a.svg";
import BusinessIcon from "@material-ui/icons/Business";
import TimelineIcon from "@material-ui/icons/Timeline";
import WarningRounded from "@material-ui/icons/WarningRounded";
import {
    adminOrganizationPermissions,
    anonymousOrganizationPermissions,
    OrganizationPermissionsDTO,
} from "../../models/members";
import {isAdmin, isUser} from "../../utils/auth";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {PageHeader} from "../common/headers";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import {BreadcrumbItem, BreadcrumbsRow} from "../common/breadcrumbs";
import AssessmentIcon from "@material-ui/icons/Assessment";
import {TagDTO} from "../../models/admin";
import {
    filterInputVariables,
    InputVariablesFilter,
    InputVariablesFilterContainer,
} from "../definitions/inputvariables/overview/InputVariablesFilter";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        withInfo: {
            display: "inline-block",
        },
        formRow: {
            margin: theme.spacing(1),
        },
        formButton: {
            display: "flex",
            justifyContent: "flex-end",
            minWidth: "14em",
        },
        editButtonWrapper: {
            display: "flex",
            justifyContent: "flex-end",
        },
        editButton: {
            marginLeft: "0.5em",
        },
    })
);

interface ValueInputProps {
    task: InputVariableTaskDTO;
    isOutdated: boolean;
}

interface ValueInputState {
    name: "saved" | "saving";
    notify: "created" | "failed" | null;
    form: boolean;
    history: boolean;
    info: boolean;
    updates: number;
    value: InputValueCreateDTO;
    newValue?: InputValueDTO;
    isOutdated: boolean;
}

const isOutdated = (task: InputVariableTaskDTO, lastGathered: Date | null) => {
    if (!lastGathered) return false;
    if (!task.inputVariable.periods.length)
        return lastGathered < new Date(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate());
    return task.inputVariable.periods.every(
        (period) =>
            lastGathered <
            new Date(
                new Date().getFullYear() -
                    (!period.repeatYear && !period.repeatMonth && !period.repeatDay ? 1 : period.repeatYear),
                new Date().getMonth() - period.repeatMonth,
                new Date().getDate() - period.repeatDay
            )
    );
};

export const ValueInput: React.FC<ValueInputProps> = (props: ValueInputProps) => {
    //== Init =================================================================
    const classes = useStyles();
    const defaultDate: string = DateUtils.todayDateString();
    const defaultValue: InputValueCreateDTO = {
        gatheredAt: defaultDate,
        value: props.task.inputVariable.options.length > 0 ? props.task.inputVariable.options[0].value.value : 0,
        unitUUID: props.task.preferredUnit?.id ?? null,
        note: "",
        municipalityInputVariableUUID: props.task.municipalityInputVariableUUID,
    };
    const initState: ValueInputState = {
        name: "saved",
        notify: null,
        form: false,
        history: false,
        info: false,
        value: defaultValue,
        updates: 0,
        isOutdated: props.isOutdated,
    };
    const [state, setState] = React.useState(initState);
    const {keycloak, initialized} = useKeycloak();
    const [t] = useTranslation(["values", "common"]);
    //== Effects ==============================================================
    React.useEffect(() => {
        if (state.name === "saving") {
            API.post<InputValueResultDTO>(`/values-input`, state.value, createApiConfig(keycloak, initialized))
                .then((res) => {
                    setState({
                        ...state,
                        name: "saved",
                        notify: "created",
                        form: false,
                        history: true,
                        value: defaultValue,
                        newValue: res.data.value,
                        updates: state.updates + 1,
                        isOutdated: isOutdated(props.task, DateUtils.stringToDate(res.data.value.gatheredAt)),
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "saved", notify: "failed"});
                });
        }
    }, [props, state, setState, keycloak, initialized, defaultValue]);
    //== Handlers =============================================================
    const handleAddValue = (): void => {
        setState({...state, form: true, notify: null});
    };
    const handleSendValue = (inputValue: InputValueCreateDTO): void => {
        setState({...state, value: inputValue, name: "saving", notify: null});
    };
    const handleCancelValue = (): void => {
        setState({...state, form: false, notify: null});
    };
    const handleExpand = (): void => {
        setState({...state, history: true, notify: null});
    };
    const handleCollapse = (): void => {
        setState({...state, history: false, notify: null});
    };
    const handleInfoOpen = (): void => {
        setState({...state, info: true, notify: null});
    };
    const handleInfoClose = (): void => {
        setState({...state, info: false, notify: null});
    };
    //== Render ===============================================================
    const notifications: JSX.Element[] = [];
    if (state.notify === "created") {
        notifications.push(
            <SuccessNotification
                key={`created_${state.newValue?.id}`}
                message={t("values_input.notifications.created")}
            />
        );
    }
    if (state.notify === "failed") {
        notifications.push(<ErrorNotification key="failed" message={t("values_input.notifications.failed")} />);
    }
    /*
    console.log("[ValuesInput] name: ");
    console.log(props.task.inputVariable.name);
    console.log("[ValuesInput] periods: ");
    console.log(props.task.inputVariable.periods);
    console.log("[ValuesInput] minValues: ");
    console.log(
        props.task.inputVariable.periods.map(
            (period) =>
                new Date(
                    new Date().getFullYear() -
                        (!period.repeatYear && !period.repeatMonth && !period.repeatDay ? 1 : period.repeatYear),
                    new Date().getMonth() - period.repeatMonth,
                    new Date().getDate() - period.repeatDay
                )
        )
    );
    */
    return (
        <Box borderBottom={2} borderColor="primary.main" style={{borderColor: defaultPalette.primary.main}}>
            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                style={{paddingTop: "0.5em", paddingBottom: "0,5em"}}
            >
                <Box>
                    {state.history ? (
                        <ExpandLessActionButton key="less" onClick={handleCollapse} />
                    ) : (
                        <ExpandMoreActionButton key="more" onClick={handleExpand} />
                    )}
                </Box>
                <Box>
                    <h3 className={classes.withInfo}>{props.task.inputVariable.name}</h3>
                </Box>
                <Box flexGrow="1">
                    <InfoActionButton onClick={handleInfoOpen} />
                </Box>
                <Box className={"classes.outdatedWarning"}>
                    {state.isOutdated ? (
                        <Box title={t("values_input.outdated")}>
                            <WarningRounded />
                        </Box>
                    ) : null}
                </Box>
                <Box className={classes.formButton}>
                    {state.form ? (
                        <Button variant="outlined" color="primary" onClick={handleCancelValue}>
                            {t("values_input.hideForm")}
                        </Button>
                    ) : (
                        <Button variant="outlined" color="primary" onClick={handleAddValue}>
                            {t("values_input.addValue")}
                        </Button>
                    )}
                </Box>
            </Box>
            {state.form && (
                <Box className={classes.formRow}>
                    <InputValueForm
                        disabled={state.name === "saving"}
                        inputVariable={props.task.inputVariable}
                        municipalityInputVariableUUID={props.task.municipalityInputVariableUUID}
                        preferredUnit={props.task.preferredUnit}
                        possibleUnits={props.task.possibleUnits}
                        value={state.value}
                        onSubmit={handleSendValue}
                    />
                </Box>
            )}
            {state.history && (
                <InputValueHistory
                    key={`${props.task.municipalityInputVariableUUID}-history-${state.updates}`}
                    task={props.task}
                    newValue={state.newValue}
                />
            )}
            {notifications}
            <InputVariableDialog
                inputVariable={props.task.inputVariable}
                open={state.info}
                handleClose={handleInfoClose}
            />
        </Box>
    );
};

export interface ValuesInputState {
    name: "loading" | "loaded" | "refreshing" | "refreshed" | "failed" | "not_found";
    task: ValuesInputTaskDTO | null;
    filteredVariableTasks: InputVariableTaskDTO[];
    filter: InputVariablesFilterContainer;
    allTags: TagDTO[];
    permissions: OrganizationPermissionsDTO;
}

interface ValuesInputProps {
    organizationId: string;
    taskUrl: string;
    simpleLoadingIndicator?: boolean;
    hideFilter?: boolean;

    onTaskLoad?(task: ValuesInputTaskDTO, onRefresh: () => void): void;
}

interface TaskWithOrderScore {
    task: InputVariableTaskDTO;
    order: number;
}

export const ValuesInput: React.FC<ValuesInputProps> = (props: ValuesInputProps) => {
    //== Init =================================================================
    const {organizationId, taskUrl} = props;
    const initState: ValuesInputState = {
        name: "loading",
        task: null,
        filteredVariableTasks: [],
        filter: {
            name: "",
            tags: [],
            tagIds: new Set<string>(),
        },
        allTags: [],
        permissions: anonymousOrganizationPermissions(organizationId),
    };
    const [state, setState] = React.useState(initState);
    const {keycloak, initialized} = useKeycloak();
    const [t] = useTranslation(["values", "common"]);
    //== Handlers =============================================================
    const handleRefresh = (): void => {
        setState({...state, name: "refreshing"});
    };

    const filteredTasks = (
        filter: InputVariablesFilterContainer,
        tasks: InputVariableTaskDTO[]
    ): InputVariableTaskDTO[] => {
        const filteredInputVariables = filterInputVariables(
            tasks.map((task) => task.inputVariable),
            filter
        );
        return tasks.filter((task) => filteredInputVariables.includes(task.inputVariable));
    };
    const handleChangeFilter = (filter: InputVariablesFilterContainer) => {
        setState({
            ...state,
            filteredVariableTasks: state.task ? filteredTasks(filter, state.task.tasks) : [],
            filter: filter,
        });
    };
    //== Effects ==============================================================
    const timeOfLastRequest = useRef(Date.now());
    React.useEffect(() => {
        if (state.name === "loading" || state.name === "refreshing") {
            const timeOfThisRequest = Date.now();
            timeOfLastRequest.current = timeOfThisRequest;
            API.get<ValuesInputTaskDTO>(taskUrl, createApiConfig(keycloak, initialized))
                .then((res) => {
                    if (timeOfThisRequest != timeOfLastRequest.current) return;
                    const nextStateName = state.name === "loading" ? "loaded" : "refreshed";
                    const inputTask: ValuesInputTaskDTO = {
                        ...res.data,
                        tasks: res.data.tasks
                            .map<TaskWithOrderScore>((task, index) => {
                                return {
                                    task: task,
                                    order:
                                        index +
                                        (!task.lastGathered ||
                                        isOutdated(task, DateUtils.stringToDate(task.lastGathered))
                                            ? 0
                                            : res.data.tasks.length),
                                };
                            })
                            .sort((a, b) => {
                                return a.order - b.order;
                            })
                            .map((taskWithOrder) => taskWithOrder.task),
                    };
                    const allTags = [...new Set(inputTask.tasks.flatMap((task) => task.inputVariable.tags))];
                    const allTagIds = allTags.map((tag) => tag.id);
                    const filteredTags = state.filter.tags.filter((filteredTag) => allTagIds.includes(filteredTag.id));
                    const nextState: ValuesInputState = {
                        ...state,
                        name: nextStateName,
                        task: inputTask,
                        allTags: allTags,
                        filteredVariableTasks: filteredTasks(state.filter, inputTask.tasks),
                        filter: {
                            ...state.filter,
                            tags: filteredTags,
                            tagIds: new Set(filteredTags.map((tag) => tag.id)),
                        },
                    };
                    if (timeOfThisRequest != timeOfLastRequest.current) return;
                    if (props.onTaskLoad) props.onTaskLoad(inputTask, handleRefresh);
                    if (!isUser(keycloak)) {
                        setState({...nextState, permissions: anonymousOrganizationPermissions(organizationId)});
                    } else if (isAdmin(keycloak)) {
                        setState({...nextState, permissions: adminOrganizationPermissions(organizationId)});
                    } else {
                        API.get<OrganizationPermissionsDTO>(
                            `/organization-permissions/${organizationId}/current-user`,
                            createApiConfig(keycloak, initialized)
                        ).then((permissionsRes) => {
                            if (timeOfThisRequest != timeOfLastRequest.current) return;
                            setState({...nextState, permissions: permissionsRes.data});
                        });
                    }
                })
                .catch((err) => {
                    if (timeOfThisRequest != timeOfLastRequest.current) return;
                    if (err?.response?.status === 404) {
                        setState({...state, name: "not_found"});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, organizationId, keycloak, initialized]);
    //== Permissions ==========================================================
    // TODO: handle more permissions for input values
    //== Render ===============================================================

    if (state.name === "loading" || state.name === "refreshing") {
        return props.simpleLoadingIndicator ? <Loading /> : <LoadingBackdrop />;
    }
    if (state.name === "not_found") {
        return <NotFound />;
    }
    if (state.name === "failed") {
        return <CommunicationError />;
    }
    if (state.task !== null) {
        return (
            <>
                {!props.hideFilter && (
                    <InputVariablesFilter
                        filter={state.filter}
                        onChange={handleChangeFilter}
                        possibleTags={state.allTags}
                    />
                )}

                {state.filteredVariableTasks.map((t) => (
                    <ValueInput
                        key={t.municipalityInputVariableUUID}
                        isOutdated={!t.lastGathered || isOutdated(t, DateUtils.stringToDate(t.lastGathered))}
                        task={t}
                    />
                ))}
                {state.filteredVariableTasks.length === 0 && (
                    <Box style={{textAlign: "center"}}>
                        <h2>{t("values_input.no_tasks")}</h2>
                        <img src={WorkingSVG} style={{maxWidth: "40em", margin: "3em"}} alt="no-input-values" />
                    </Box>
                )}
                {state.name === "refreshed" && <InfoNotification message={t("values_input.notifications.refreshed")} />}
            </>
        );
    }
    return <CommunicationError />;
};

interface MunicipalityParams {
    organizationId: string;
}

export const MunicipalityValuesInput: React.FC = () => {
    //== Init =================================================================
    const {organizationId} = useParams<MunicipalityParams>();
    const [t] = useTranslation(["values", "common"]);
    const {keycloak, initialized} = useKeycloak();
    const [state, setState] = React.useState({
        task: null as ValuesInputTaskDTO | null,
        handleRefresh: () => {
            /* do nothing by default */
        },
    });
    const [excelState, setExcelState] = React.useState({
        downloading: false,
        analyzing: false,
        importing: false,
        importResult: null as ImportResultDTO | null,
        error: null as string | null,
        pendingFile: null as File | null,
        missingDateRows: null as Array<{row: number; variableName: string}> | null,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const classes = useStyles();

    //== Handlers =============================================================
    function handleTaskLoad(task: ValuesInputTaskDTO, handleRefresh: () => void) {
        setState({task, handleRefresh});
    }

    const handleDownloadExcel = async (): Promise<void> => {
        setExcelState((s) => ({...s, downloading: true, error: null}));
        try {
            const response = await API.get<ArrayBuffer>(`/organizations/${organizationId}/import/excel`, {
                ...createApiConfig(keycloak, initialized),
                responseType: "arraybuffer",
            });
            const contentDisposition = (response.headers["content-disposition"] as string) || "";
            const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
            const filename = filenameMatch?.[1] ?? "SCC_hodnoty.xlsx";
            const el = Utils.download(filename, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", [
                response.data,
            ]);
            el.click();
            document.body.removeChild(el);
            setExcelState((s) => ({...s, downloading: false}));
        } catch (_err) {
            setExcelState((s) => ({...s, downloading: false, error: t("actions.excel.downloadError")}));
        }
    };

    const handleImportExcelClick = (): void => {
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = event.target.files?.[0];
        if (!file) return;
        event.target.value = "";
        setExcelState((s) => ({...s, analyzing: true, error: null, importResult: null}));
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await API.post<{missingDateRows: Array<{row: number; variableName: string}>}>(
                `/organizations/${organizationId}/import/excel/analyze`,
                formData,
                createApiConfig(keycloak, initialized)
            );
            setExcelState((s) => ({
                ...s,
                analyzing: false,
                pendingFile: file,
                missingDateRows: response.data.missingDateRows,
            }));
        } catch (_err) {
            setExcelState((s) => ({...s, analyzing: false, error: t("actions.excel.importError")}));
        }
    };

    const handleImportConfirm = async (useFallbackDate: boolean): Promise<void> => {
        const file = excelState.pendingFile;
        if (!file) return;
        setExcelState((s) => ({...s, pendingFile: null, missingDateRows: null, importing: true}));
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await API.post<ImportResultDTO>(
                `/organizations/${organizationId}/import/excel?useFallbackDate=${useFallbackDate}`,
                formData,
                createApiConfig(keycloak, initialized)
            );
            setExcelState((s) => ({...s, importing: false, importResult: response.data}));
        } catch (_err) {
            setExcelState((s) => ({...s, importing: false, error: t("actions.excel.importError")}));
        }
    };

    const handleImportDialogClose = (): void => {
        setExcelState((s) => ({...s, pendingFile: null, missingDateRows: null}));
    };

    //== Render ===============================================================
    return (
        <Container>
            <BreadcrumbsRow>
                <BreadcrumbItem
                    name={state.task?.municipality.name || t("organization:organization.municipality")}
                    route={ROUTES.organization.create(organizationId)}
                />
                <BreadcrumbItem name={t("actions.valuesInput")} />
            </BreadcrumbsRow>
            <PageHeader title={t("values_input.title", {municipality: state.task?.municipality.name || ""})}>
                <LinkActionButton
                    title={t("actions.organization")}
                    to={ROUTES.organization.create(organizationId)}
                    icon={<BusinessIcon />}
                />
                <LinkActionButton
                    title={t("actions.trackedIndicatorsBrowser")}
                    to={ROUTES.trackedIndicatorGroups.create(organizationId)}
                    icon={<TimelineIcon />}
                />
                <CustomActionButton title={t("actions.refresh")} onClick={state.handleRefresh} icon={<RefreshIcon />} />
            </PageHeader>
            <Box className={classes.editButtonWrapper}>
                <Button
                    variant="outlined"
                    className={classes.editButton}
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadExcel}
                    disabled={excelState.downloading || excelState.importing}
                >
                    {excelState.downloading ? t("actions.excel.downloading") : t("actions.downloadExcel")}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{display: "none"}}
                    onChange={handleFileSelected}
                />
                <Button
                    variant="outlined"
                    className={classes.editButton}
                    startIcon={<PublishIcon />}
                    onClick={handleImportExcelClick}
                    disabled={excelState.downloading || excelState.importing || excelState.analyzing}
                >
                    {excelState.analyzing
                        ? t("actions.excel.analyzing")
                        : excelState.importing
                        ? t("actions.excel.importing")
                        : t("actions.importExcel")}
                </Button>
                <Button
                    component={Link}
                    to={ROUTES.valuesInputSummary.create(organizationId)}
                    variant="contained"
                    className={classes.editButton}
                    startIcon={<AssessmentIcon />}
                >
                    {t("actions.inputValuesSummary")}
                </Button>
            </Box>
            {excelState.pendingFile && excelState.missingDateRows !== null && (
                <Dialog open onClose={handleImportDialogClose} maxWidth="sm" fullWidth>
                    <DialogTitle>{t("actions.excel.fallbackDateDialogTitle")}</DialogTitle>
                    <DialogContent>
                        {excelState.missingDateRows.length === 0 ? (
                            <Typography>{t("actions.excel.fallbackDateDialogNoMissing")}</Typography>
                        ) : (
                            <>
                                <Typography>{t("actions.excel.fallbackDateDialogText")}</Typography>
                                <Box mt={2} mb={1}>
                                    {excelState.missingDateRows.map((r) => (
                                        <Box key={r.row} display="flex" alignItems="center" mb={0.5}>
                                            <Box
                                                component="span"
                                                style={{
                                                    background: "#FEF3C7",
                                                    border: "1px solid #F59E0B",
                                                    borderRadius: 4,
                                                    padding: "2px 8px",
                                                    marginRight: 8,
                                                    fontSize: "0.8rem",
                                                    whiteSpace: "nowrap" as const,
                                                }}
                                            >
                                                {t("actions.excel.row")} {r.row}
                                            </Box>
                                            <Typography variant="body2">{r.variableName}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {excelState.missingDateRows.length > 0 ? (
                            <>
                                <Button onClick={handleImportDialogClose}>
                                    {t("actions.excel.fallbackDateDecline")}
                                </Button>
                                <Button onClick={() => handleImportConfirm(true)} color="primary" variant="contained">
                                    {t("actions.excel.fallbackDateConfirm")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={handleImportDialogClose}>{t("actions.excel.importClose")}</Button>
                                <Button onClick={() => handleImportConfirm(false)} color="primary" variant="contained">
                                    {t("actions.excel.importConfirm")}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            )}
            {excelState.error && <ErrorNotification message={excelState.error} />}
            {excelState.importResult && (
                <Dialog open onClose={() => setExcelState((s) => ({...s, importResult: null}))} maxWidth="sm" fullWidth>
                    <DialogTitle>{t("actions.excel.importTitle")}</DialogTitle>
                    <DialogContent>
                        <Box
                            p={2}
                            mb={2}
                            style={{
                                background: excelState.importResult.failedCount === 0 ? "#F0FDF4" : "#FFFBEB",
                                border: `1px solid ${
                                    excelState.importResult.failedCount === 0 ? "#86EFAC" : "#FCD34D"
                                }`,
                                borderRadius: 8,
                            }}
                        >
                            <Typography variant="h6" style={{fontWeight: 700}}>
                                {t("actions.excel.importSuccess", {
                                    success: excelState.importResult.successCount,
                                    total: excelState.importResult.totalRows,
                                })}
                            </Typography>
                            {excelState.importResult.failedCount > 0 && (
                                <Typography variant="body2" style={{marginTop: 4}}>
                                    {t("actions.excel.importFailedCount", {count: excelState.importResult.failedCount})}
                                </Typography>
                            )}
                        </Box>
                        {excelState.importResult.failures.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2">{t("actions.excel.importFailures")}</Typography>
                                {excelState.importResult.failures.map((f, i) => (
                                    <Box key={i} mt={1} p={1} border={1} borderColor="error.main" borderRadius={4}>
                                        <Typography variant="body2">
                                            <strong>
                                                {t("actions.excel.row")} {f.row}: {f.variableName}
                                            </strong>
                                        </Typography>
                                        <Typography variant="body2" color="error">
                                            {f.errorDetail}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setExcelState((s) => ({...s, importResult: null}))} color="primary">
                            {t("actions.excel.importClose")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <ValuesInput
                organizationId={organizationId}
                taskUrl={`/values-input/task/${organizationId}`}
                onTaskLoad={handleTaskLoad}
            />
        </Container>
    );
};

export default MunicipalityValuesInput;

interface GroupValuesInputParams {
    organizationId: string;
    trackedIndicatorGroupId: string;
}

export const GroupValuesInput: React.FC = () => {
    //== Init =================================================================
    const {organizationId, trackedIndicatorGroupId} = useParams<GroupValuesInputParams>();
    const [t] = useTranslation(["values", "common", "tracked-indicators"]);
    const [state, setState] = React.useState({
        task: null as ValuesInputTaskDTO | null,
        handleRefresh: () => {
            /* do nothing by default */
        },
    });
    const classes = useStyles();

    //== Handlers =============================================================
    function handleTaskLoad(task: ValuesInputTaskDTO, handleRefresh: () => void) {
        setState({task, handleRefresh});
    }

    //== Render ===============================================================
    return (
        <Container>
            <BreadcrumbsRow>
                <BreadcrumbItem
                    name={state.task?.municipality.name || t("organization:organization.municipality")}
                    route={ROUTES.organization.create(organizationId)}
                />
                <BreadcrumbItem
                    name={t("tracked-indicators:trackedIndicatorGroup.trackedIndicatorGroup")}
                    route={ROUTES.trackedIndicatorsBrowser.create(organizationId, trackedIndicatorGroupId)}
                />
                <BreadcrumbItem name={t("actions.valuesInput")} />
            </BreadcrumbsRow>
            <PageHeader title={t("values_input.title", {municipality: state.task?.municipality.name || ""})}>
                <LinkActionButton
                    title={t("actions.organization")}
                    to={ROUTES.organization.create(organizationId)}
                    icon={<BusinessIcon />}
                />
                <LinkActionButton
                    title={t("actions.trackedIndicatorsBrowser")}
                    to={ROUTES.trackedIndicatorsBrowser.create(organizationId, trackedIndicatorGroupId)}
                    icon={<TimelineIcon />}
                />
                <CustomActionButton title={t("actions.refresh")} onClick={state.handleRefresh} icon={<RefreshIcon />} />
            </PageHeader>
            <ValuesInput
                organizationId={organizationId}
                taskUrl={`/values-input/task/tracked-indicator-group/${trackedIndicatorGroupId}`}
                onTaskLoad={handleTaskLoad}
            />
        </Container>
    );
};
